import { localTodayYmd, parseDateOnly } from "@mediatechgroup/calendar-date";

/**
 * Client-side mirror of backend services/ageEligibility.js. Use this on
 * any surface that lets a user pick or assign a program — the wizard,
 * member self-enroll, staff Edit Athlete — to surface eligibility info
 * before submission. The backend re-validates regardless; this is purely
 * for UX (disable/annotate ineligible programs, show a confirmation
 * popup on staff overrides).
 *
 * Reference date precedence: caller-provided startDate → today.
 * Programs typically have a start_date too; pass it in if you have it.
 */

export type AgeEligibilityStatus =
  | "ok"
  | "too_young"
  | "too_old"
  | "unknown_dob"
  | "no_limits";

export interface AgeEligibilityResult {
  eligible: boolean;
  age: number | null;
  minAge: number | null;
  maxAge: number | null;
  asOfDate: string;
  status: AgeEligibilityStatus;
  reason: string | null;
}

export function computeAgeOnDate(
  dob: string | Date | null | undefined,
  asOfDate: string | Date | null | undefined,
): number | null {
  if (!dob) return null;
  // Calendar dates parse as LOCAL midnight — `new Date("2018-06-30")` is UTC
  // midnight, i.e. June 29 in US zones, which made a child a year younger on
  // their birthday. See @mediatechgroup/calendar-date's date-only helpers.
  const dobDate = parseDateOnly(dob);
  if (!dobDate) return null;
  const ref = asOfDate ? parseDateOnly(asOfDate) : new Date();
  if (!ref) return null;

  let age = ref.getFullYear() - dobDate.getFullYear();
  const beforeBirthday =
    ref.getMonth() < dobDate.getMonth() ||
    (ref.getMonth() === dobDate.getMonth() && ref.getDate() < dobDate.getDate());
  if (beforeBirthday) age -= 1;
  return age;
}

export interface EvaluateInput {
  minAge?: number | null;
  maxAge?: number | null;
  dob?: string | Date | null;
  asOfDate?: string | Date | null;
}

export function evaluateProgramAgeEligibility({
  minAge,
  maxAge,
  dob,
  asOfDate,
}: EvaluateInput): AgeEligibilityResult {
  const min =
    typeof minAge === "number" && Number.isFinite(minAge) ? minAge : null;
  const max =
    typeof maxAge === "number" && Number.isFinite(maxAge) ? maxAge : null;
  const ref = (asOfDate ? parseDateOnly(asOfDate) : null) ?? new Date();
  const refIso = localTodayYmd(ref);

  if (min == null && max == null) {
    return {
      eligible: true,
      age: dob ? computeAgeOnDate(dob, ref) : null,
      minAge: null,
      maxAge: null,
      asOfDate: refIso,
      status: "no_limits",
      reason: null,
    };
  }

  const age = computeAgeOnDate(dob, ref);
  if (age == null) {
    return {
      eligible: false,
      age: null,
      minAge: min,
      maxAge: max,
      asOfDate: refIso,
      status: "unknown_dob",
      reason:
        "This program has an age requirement and we don't have a date of birth on file for the athlete.",
    };
  }

  if (min != null && age < min) {
    return {
      eligible: false,
      age,
      minAge: min,
      maxAge: max,
      asOfDate: refIso,
      status: "too_young",
      reason: `This program requires participants to be at least ${min} years old by ${refIso}. Athlete will be ${age} on that date.`,
    };
  }
  if (max != null && age > max) {
    return {
      eligible: false,
      age,
      minAge: min,
      maxAge: max,
      asOfDate: refIso,
      status: "too_old",
      reason: `This program is for participants ${max} years old and under by ${refIso}. Athlete will be ${age} on that date.`,
    };
  }

  return {
    eligible: true,
    age,
    minAge: min,
    maxAge: max,
    asOfDate: refIso,
    status: "ok",
    reason: null,
  };
}

/**
 * Convenience wrapper for staff confirmation popups: returns a short
 * one-line summary suitable for "are you sure?" prompts.
 */
export function describeAgeMismatch(result: AgeEligibilityResult): string {
  if (result.eligible) return "";
  const ageStr = result.age == null ? "unknown age" : `${result.age} years old`;
  const window =
    result.minAge != null && result.maxAge != null
      ? `${result.minAge}–${result.maxAge}`
      : result.minAge != null
      ? `${result.minAge}+`
      : result.maxAge != null
      ? `up to ${result.maxAge}`
      : "";
  if (result.status === "too_young") {
    return `Athlete is ${ageStr}. Program requires ${window} years.`;
  }
  if (result.status === "too_old") {
    return `Athlete is ${ageStr}. Program is for ${window} years.`;
  }
  if (result.status === "unknown_dob") {
    return "No date of birth on file. Program has an age requirement.";
  }
  return result.reason || "Athlete does not meet the program age range.";
}
